import { useState } from 'react'
import { Header, Footer } from '@/components/layout'
import { SEO } from '@/components/seo'

const steps = [
  {
    number: '01',
    title: 'Submit a Partner Application',
    description: 'Complete the partner application form. Applications are reviewed and responded to within 24–48 business hours.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.8" stroke="#08C6EA">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
      </svg>
    ),
  },
  {
    number: '02',
    title: 'Refer Clients via Your Unique Link',
    description: 'Share your assigned referral link with clients who need cloud accounting, invoicing, payroll, or expense management software.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.8" stroke="#08C6EA">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
      </svg>
    ),
  },
  {
    number: '03',
    title: 'Earn Recurring Commission',
    description: 'Referred clients receive 20% off their first subscription period. You earn 30% of their subscription fee on a recurring basis for the duration of their engagement.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.8" stroke="#08C6EA">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
      </svg>
    ),
  },
]

const benefits = [
  {
    title: 'Recurring Commission Income',
    description: 'Earn 30% of each referred client\'s subscription fee on a continuing basis — not a one-time referral bonus.',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
      </svg>
    ),
  },
  {
    title: 'No Referral Cap',
    description: 'There is no limit on the number of clients you may refer. Commission scales proportionally with each active referral.',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
      </svg>
    ),
  },
  {
    title: 'Partner Dashboard',
    description: 'Monitor referral activity, conversion status, and commission earnings in real time through a dedicated partner portal.',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" />
      </svg>
    ),
  },
  {
    title: 'Monthly Commission Payouts',
    description: 'Commissions are calculated at the close of each billing cycle and disbursed monthly directly to your nominated account.',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
      </svg>
    ),
  },
  {
    title: 'Dedicated Account Manager',
    description: 'Each approved partner is assigned a dedicated account manager to assist with client onboarding and ongoing programme support.',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
      </svg>
    ),
  },
  {
    title: 'Co-Branded Materials',
    description: 'Access a library of branded materials, email templates, and presentation decks to support client introductions.',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    ),
  },
]

const faqs = [
  {
    question: 'Who is eligible to participate in the partner programme?',
    answer: 'The programme is open to registered accounting firms, bookkeeping practices, and licensed CPAs. Each application is individually reviewed to confirm suitability.',
  },
  {
    question: 'What discount does a referred client receive?',
    answer: 'Clients referred through the programme receive 20% off their first month or first year on any paid plan, applied automatically at the point of subscription.',
  },
  {
    question: 'How is the 30% commission calculated?',
    answer: 'Commission is calculated at 30% of the net subscription fee paid by each referred client, whether on a monthly or annual billing cycle. For example, a client subscribed to the Business plan (Rs 990/month) generates a commission of Rs 297 per month for the duration of their subscription.',
  },
  {
    question: 'Is there a limit on the number of referrals?',
    answer: 'No. There is no ceiling on the number of clients you may refer. Commission accrues for each active referred subscription.',
  },
  {
    question: 'When and how are commissions paid?',
    answer: 'Commissions are calculated at the end of each billing cycle and disbursed monthly via bank transfer. A minimum payout threshold of Rs 5,000 applies.',
  },
  {
    question: 'How are commissions affected by a client plan upgrade?',
    answer: 'Commission adjusts automatically to reflect the client\'s current plan. You will always earn 30% of the subscription fee applicable at the time of each billing cycle.',
  },
  {
    question: 'What happens if a referred client cancels their subscription?',
    answer: 'Commission ceases to accrue upon cancellation. Should the client re-subscribe using your referral link, commission will resume from the date of re-subscription.',
  },
]


export default function ReferralPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Accounting Firm Partner Programme"
        description="Earn recurring commission by onboarding your clients onto Ledger. A partner programme for accounting firms and practices in Sri Lanka."
        canonical="/referral"
      />
      <Header />

      <main>
        {/* Hero */}
        <section className="pt-32 pb-20 bg-white border-b border-surface-200">
          <div className="mx-auto max-w-7xl section-padding">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold text-surface-900 tracking-widest uppercase mb-4">Partner Programme</p>
              <h1 className="text-4xl md:text-5xl font-bold text-surface-900 mb-6 leading-tight">
                Accounting Firm Partner Programme
              </h1>
              <p className="text-lg text-surface-600 mb-8 leading-relaxed">
                Ledger's partner programme is designed for accounting firms and CPA practices seeking to generate recurring revenue by introducing clients to a platform that complements their existing advisory services. Referred clients receive a 20% introductory discount; partners earn a 30% recurring commission for the lifetime of each referred subscription.
              </p>
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <a href="/partner-application" className="btn btn-primary px-8 py-3 text-base">
                  Apply for Partnership
                </a>
                <a href="#programme-overview" className="btn btn-secondary px-8 py-3 text-base">
                  Programme Overview
                </a>
              </div>
            </div>

            {/* Key terms summary */}
            <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-px bg-surface-200 border border-surface-200 rounded-xl overflow-hidden">
              <div className="bg-white px-6 py-5">
                <div className="text-2xl font-bold text-surface-900">30%</div>
                <div className="text-sm text-surface-500 mt-1">Recurring partner commission</div>
              </div>
              <div className="bg-white px-6 py-5">
                <div className="text-2xl font-bold text-surface-900">20%</div>
                <div className="text-sm text-surface-500 mt-1">Client introductory discount</div>
              </div>
              <div className="bg-white px-6 py-5">
                <div className="text-2xl font-bold text-surface-900">None</div>
                <div className="text-sm text-surface-500 mt-1">Referral cap</div>
              </div>
              <div className="bg-white px-6 py-5">
                <div className="text-2xl font-bold text-surface-900">Monthly</div>
                <div className="text-sm text-surface-500 mt-1">Commission payout schedule</div>
              </div>
            </div>
          </div>
        </section>

        {/* Programme Overview */}
        <section id="programme-overview" className="py-20 bg-surface-50">
          <div className="mx-auto max-w-7xl section-padding">
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-surface-900">How the Programme Works</h2>
              <p className="mt-2 text-surface-600">Three steps from application to commission.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {steps.map((step) => (
                <div key={step.number} className="card p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#e0f9fd' }}>
                      {step.icon}
                    </div>
                    <span className="text-xs font-bold text-surface-400 tracking-widest">{step.number}</span>
                  </div>
                  <h3 className="text-base font-semibold text-surface-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-surface-600 leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Partner and client terms */}
        <section className="py-20 bg-white">
          <div className="mx-auto max-w-7xl section-padding">
            <div className="mb-12 text-center">
              <h2 className="text-2xl font-bold text-surface-900">Programme Terms</h2>
              <p className="mt-2 text-surface-600 max-w-xl mx-auto">The programme is structured to provide measurable value to both the referring firm and the referred client.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <div className="card p-8">
                <p className="text-xs font-semibold text-surface-500 uppercase tracking-widest mb-4">For the Partner Firm</p>
                <h3 className="text-lg font-semibold text-surface-900 mb-4">Commission Terms</h3>
                <ul className="space-y-3 text-sm text-surface-600">
                  {[
                    <span><strong className="text-surface-800">30% recurring commission</strong> on each referred client's subscription fee</span>,
                    <span>Commission is ongoing for the lifetime of the referred subscription — not a one-time payment</span>,
                    <span>No ceiling on the number of clients that may be referred</span>,
                    <span>Real-time tracking of referrals and commission via partner dashboard</span>,
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: '#dcfce7' }}>
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth="3" stroke="#16a34a"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-8">
                <p className="text-xs font-semibold text-surface-500 uppercase tracking-widest mb-4">For the Referred Client</p>
                <h3 className="text-lg font-semibold text-surface-900 mb-4">Client Benefits</h3>
                <ul className="space-y-3 text-sm text-surface-600">
                  {[
                    <span><strong className="text-surface-800">20% discount on the first month or year</strong> of any paid subscription plan</span>,
                    <span>Referral from a trusted professional adviser familiar with their financial requirements</span>,
                    <span>Assisted onboarding with support from both the partner firm and Ledger's team</span>,
                    <span>Platform integration designed to complement the firm's existing bookkeeping workflow</span>,
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: '#dcfce7' }}>
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth="3" stroke="#16a34a"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Commission structure */}
        <section className="py-20 bg-surface-50">
          <div className="mx-auto max-w-7xl section-padding">
            <div className="mb-12 text-center">
              <h2 className="text-2xl font-bold text-surface-900">Commission Structure</h2>
              <p className="mt-2 text-surface-600">30% of the net subscription fee per referred client, recurring for the lifetime of the subscription.</p>
            </div>

            <div className="max-w-3xl mx-auto overflow-hidden rounded-xl border border-surface-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-50 border-b border-surface-200">
                    <th className="text-left px-6 py-4 text-surface-600 font-semibold">Plan</th>
                    <th className="text-right px-6 py-4 text-surface-600 font-semibold">Monthly price</th>
                    <th className="text-right px-6 py-4 text-surface-600 font-semibold">Monthly commission</th>
                    <th className="text-right px-6 py-4 text-surface-600 font-semibold">Annual price</th>
                    <th className="text-right px-6 py-4 text-surface-600 font-semibold">Annual commission</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-6 py-4 font-medium text-surface-900">Free</td>
                    <td className="px-6 py-4 text-right text-surface-600">Rs 0</td>
                    <td className="px-6 py-4 text-right text-surface-500 italic">Free plan</td>
                    <td className="px-6 py-4 text-right text-surface-600">Rs 0</td>
                    <td className="px-6 py-4 text-right text-surface-500 italic">—</td>
                  </tr>
                  <tr className="border-b border-surface-100" style={{ backgroundColor: '#e0f9fd40' }}>
                    <td className="px-6 py-4 font-medium text-surface-900">
                      Business
                      <span className="ml-2 text-xs px-2 py-0.5 rounded-full font-normal" style={{ backgroundColor: '#e0f9fd', color: '#08C6EA' }}>Popular</span>
                    </td>
                    <td className="px-6 py-4 text-right text-surface-600">Rs 990</td>
                    <td className="px-6 py-4 text-right font-semibold" style={{ color: '#08C6EA' }}>Rs 297</td>
                    <td className="px-6 py-4 text-right text-surface-600">Rs 9,900</td>
                    <td className="px-6 py-4 text-right font-semibold" style={{ color: '#08C6EA' }}>Rs 2,970</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium text-surface-900">Business Plus</td>
                    <td className="px-6 py-4 text-right text-surface-600">Rs 2,900</td>
                    <td className="px-6 py-4 text-right font-semibold" style={{ color: '#08C6EA' }}>Rs 870</td>
                    <td className="px-6 py-4 text-right text-surface-600">Rs 29,000</td>
                    <td className="px-6 py-4 text-right font-semibold" style={{ color: '#08C6EA' }}>Rs 8,700</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm text-surface-500 mt-4 text-center">
              Figures shown per referred client. Commissions are recurring for the lifetime of the subscription.
            </p>
          </div>
        </section>


        {/* Benefits */}
        <section className="py-20 bg-surface-50">
          <div className="mx-auto max-w-7xl section-padding">
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-surface-900">Partner Programme Inclusions</h2>
              <p className="mt-2 text-surface-600">Resources and support available to approved partner firms.</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit) => (
                <div key={benefit.title} className="card card-hover p-6">
                  <div className="icon-box icon-box-cyan mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="font-semibold text-surface-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-surface-600 leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-white">
          <div className="mx-auto max-w-7xl section-padding">
            <div className="max-w-2xl mx-auto">
              <div className="mb-12 text-center">
                <h2 className="text-2xl font-bold text-surface-900">Frequently Asked Questions</h2>
              </div>

              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <div key={index} className="border border-surface-200 rounded-lg overflow-hidden">
                    <button
                      className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 hover:bg-surface-50 transition-colors"
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    >
                      <span className="font-medium text-surface-900">{faq.question}</span>
                      <svg
                        className={`w-5 h-5 text-surface-400 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                      </svg>
                    </button>
                    {openFaq === index && (
                      <div className="px-6 pb-5 text-sm text-surface-600 leading-relaxed border-t border-surface-100 pt-4">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-surface-50">
          <div className="mx-auto max-w-7xl section-padding">
            <div className="max-w-2xl">
              <h2 className="text-2xl md:text-3xl font-bold text-surface-900 mb-4">
                Apply to the Partner Programme
              </h2>
              <p className="text-surface-600 mb-8">
                Applications are accepted from registered accounting firms, bookkeeping practices, and licensed CPAs. The review process takes 24–48 business hours. For enquiries, contact <a href="mailto:partners@ledger.lk" style={{ color: '#08C6EA' }}>partners@ledger.lk</a>.
              </p>
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <a
                  href="/partner-application"
                  className="btn btn-primary px-8 py-3 text-base font-semibold"
                >
                  Submit Application
                </a>
                <a
                  href="/pricing"
                  className="btn btn-secondary px-8 py-3 text-base"
                >
                  View Pricing Plans
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}
